import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  MapPin, Globe, Film, BadgeCheck, UserX, ExternalLink, Users, UserPlus, UserCheck, Star,
} from 'lucide-react';
import { guildAuthUrl } from '../lib/guildUrl';
import { safeUrl } from '../lib/safeUrl';

const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
};

const AVAILABILITY_LABELS = {
  available: 'Available for work',
  open_to_offers: 'Open to offers',
  unavailable: 'Unavailable',
};

const AVAILABILITY_DOT = {
  available: 'bg-emerald-400',
  open_to_offers: 'bg-amber-400',
  unavailable: 'bg-zinc-500',
};

const WORK_TYPE_LABELS = {
  movie: 'Movie',
  tv_show: 'Series',
  documentary: 'Documentary',
  short_film: 'Short Film',
  bonus: 'Bonus',
};

function WorkCard({ work, subtitle }) {
  const inner = (
    <div className="group">
      <div
        className="aspect-[2/3] rounded-xl overflow-hidden mb-2"
        style={{
          background: 'hsl(var(--kinora-card-hi, var(--card)))',
          border: '1px solid hsl(var(--kinora-line-soft, var(--border)))',
        }}
      >
        {work.poster_url ? (
          <img
            src={work.poster_url}
            alt={work.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film className="w-6 h-6 text-muted-foreground" />
          </div>
        )}
      </div>
      <div className="text-[15px] group-hover:text-primary transition-colors truncate">
        {work.title}
      </div>
      <div className="text-[12px] text-muted-foreground truncate">
        {subtitle}
      </div>
    </div>
  );

  return work.slug ? <Link to={`/watch/${work.slug}`}>{inner}</Link> : inner;
}

function SectionHeading({ label, title }) {
  return (
    <>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="text-[10px] tracking-[0.28em] uppercase text-primary whitespace-nowrap">
          {label}
        </span>
        <div
          className="h-px flex-1"
          style={{ background: 'hsl(var(--kinora-line-soft, var(--border)))' }}
        />
      </div>
      <h2
        className="text-xl font-semibold tracking-tight mb-6"
      >
        {title}
      </h2>
    </>
  );
}

export default function Creator() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [creator, setCreator] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    supabase
      .rpc('public_creator_page', { p_user_id: id })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error('Creator page error:', error);
        setCreator(data || null);
        setFollowerCount(data?.follower_count ?? 0);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (creator && user) {
      checkFollowStatus();
    } else {
      setIsFollowing(false);
    }
  }, [creator, user]);

  const checkFollowStatus = async () => {
    if (!user || !creator) return;
    try {
      const { data } = await supabase
        .from('creator_followers')
        .select('id')
        .eq('user_id', user.id)
        .eq('creator_id', creator.user_id)
        .maybeSingle();
      setIsFollowing(!!data);
    } catch {
      setIsFollowing(false);
    }
  };

  const toggleFollow = useCallback(async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!creator || user.id === creator.user_id) return;

    try {
      if (isFollowing) {
        const { error } = await supabase
          .from('creator_followers')
          .delete()
          .eq('user_id', user.id)
          .eq('creator_id', creator.user_id);
        if (error) throw error;

        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
        toast({ title: 'Unfollowed creator' });
      } else {
        const { error } = await supabase
          .from('creator_followers')
          .insert({ user_id: user.id, creator_id: creator.user_id });
        if (error) throw error;

        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
        toast({ title: 'Following creator' });
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      toast({ title: 'Failed to update follow', variant: 'destructive' });
    }
  }, [user, creator, isFollowing, navigate, toast]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 space-y-6">
        <div className="flex items-center gap-6">
          <Skeleton className="w-24 h-24 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="container mx-auto px-4 py-24 text-center space-y-4">
        <UserX className="w-10 h-10 mx-auto text-muted-foreground" />
        <h1 className="text-2xl font-bold">Creator not found</h1>
        <p className="text-muted-foreground text-sm">
          This profile doesn't exist or isn't public.
        </p>
        <Button asChild variant="outline">
          <Link to="/browse">Browse Kinora</Link>
        </Button>
      </div>
    );
  }

  const skillsByCategory = (creator.skills || []).reduce((acc, skill) => {
    (acc[skill.category] = acc[skill.category] || []).push(skill.name);
    return acc;
  }, {});

  return (
    <div className="container mx-auto px-4 py-12 max-w-5xl space-y-14">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center gap-6">
        {creator.avatar_url ? (
          <img
            src={creator.avatar_url}
            alt={creator.display_name}
            className="w-24 h-24 rounded-full object-cover shrink-0"
          />
        ) : (
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold shrink-0"
            style={{
              background: 'hsl(var(--primary) / 0.16)',
              color: 'hsl(var(--primary))',
            }}
          >
            {creator.display_name?.[0]}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1
              className="tracking-tight font-bold"
              style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}
            >
              {creator.display_name}
            </h1>
            <Badge variant="secondary" className="gap-1">
              <BadgeCheck className="w-3.5 h-3.5 text-primary" /> Kinora Guild
            </Badge>
          </div>
          {creator.headline && (
            <p className="text-muted-foreground mt-1">{creator.headline}</p>
          )}
          {creator.review_count > 0 && creator.review_avg != null && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              {creator.review_avg} · {creator.review_count} endorsement{creator.review_count === 1 ? '' : 's'}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              {formatNumber(followerCount)} followers
            </span>
            {creator.availability && (
              <span className="flex items-center gap-1.5">
                <span
                  className={`w-2 h-2 rounded-full ${AVAILABILITY_DOT[creator.availability] || ''}`}
                />
                {AVAILABILITY_LABELS[creator.availability] || creator.availability}
              </span>
            )}
            {creator.location && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {creator.location}
              </span>
            )}
            {creator.website_url && (
              <a
                href={safeUrl(creator.website_url)}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 hover:text-primary transition-colors"
              >
                <Globe className="w-3.5 h-3.5" /> Website
              </a>
            )}
            {creator.reel_url && (
              <a
                href={safeUrl(creator.reel_url)}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 hover:text-primary transition-colors"
              >
                <Film className="w-3.5 h-3.5" /> Reel
              </a>
            )}
          </div>
        </div>
        {user?.id !== creator.user_id && (
          <Button
            onClick={toggleFollow}
            variant={isFollowing ? 'outline' : 'default'}
            size="lg"
            className="rounded-full px-8 shrink-0"
          >
            {isFollowing ? (
              <>
                <UserCheck className="w-5 h-5 mr-2" />
                Following
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5 mr-2" />
                Follow
              </>
            )}
          </Button>
        )}
      </header>

      {/* Bio + skills */}
      {(creator.bio || Object.keys(skillsByCategory).length > 0) && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {creator.bio && (
            <div className="md:col-span-2">
              <SectionHeading index="01" label="About" title="About" />
              <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-foreground/90">
                {creator.bio}
              </p>
            </div>
          )}
          {Object.keys(skillsByCategory).length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-primary mb-4">
                Disciplines
              </div>
              <div className="space-y-4">
                {Object.entries(skillsByCategory).map(([category, names]) => (
                  <div key={category}>
                    <div className="text-[9px] tracking-[0.22em] uppercase text-muted-foreground mb-2">
                      {category}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {names.map(name => (
                        <Badge key={name} variant="secondary">
                          {name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Verified credits */}
      {creator.credits?.length > 0 && (
        <section>
          <SectionHeading index="02" label="On Kinora" title="Verified credits" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {creator.credits.map((credit, i) => (
              <WorkCard key={`${credit.work.id}-${i}`} work={credit.work} subtitle={credit.role} />
            ))}
          </div>
        </section>
      )}

      {/* Portfolio */}
      {creator.portfolio?.length > 0 && (
        <section>
          <SectionHeading index="03" label="Selected work" title="Portfolio" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {creator.portfolio.map((item, i) => {
              const image = item.image_url || item.work?.poster_url;
              const body = (
                <div
                  className="rounded-2xl overflow-hidden h-full"
                  style={{
                    background: 'hsl(var(--kinora-card-hi, var(--card)))',
                    border: '1px solid hsl(var(--kinora-line-soft, var(--border)))',
                  }}
                >
                  {image ? (
                    <img src={image} alt={item.title} className="w-full aspect-video object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-video flex items-center justify-center">
                      <Film className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="text-[16px]">{item.title}</div>
                    <div className="text-[12px] text-muted-foreground">
                      {[item.credit, item.year].filter(Boolean).join(' · ')}
                    </div>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              );
              return item.url ? (
                <a key={i} href={safeUrl(item.url)} target="_blank" rel="noreferrer" className="block hover:opacity-90 transition-opacity">
                  {body}
                </a>
              ) : (
                <div key={i}>{body}</div>
              );
            })}
          </div>
        </section>
      )}

      <section
        className="mt-12 rounded-xl p-6 text-center"
        style={{
          background: 'hsl(var(--kinora-card-hi, var(--card)))',
          border: '1px solid hsl(var(--kinora-line-soft, var(--border)))',
        }}
      >
        <h2 className="text-xl font-semibold mb-2">This could be you</h2>
        <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
          Kinora Guild is where indie filmmakers build crews, share projects, and get credited on the work they help create.
        </p>
        <Button asChild>
          <a href={guildAuthUrl()} target="_blank" rel="noreferrer">
            Join the Kinora Guild
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </section>
    </div>
  );
}
